import { AfterViewInit, Component, ElementRef, inject, OnDestroy, ViewChild } from '@angular/core';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import {
  BookingAddOns,
  GUEST_INSURANCE_FEE_PRODUCT_KEY,
  GUEST_PREMIUM_INSURANCE_FEE_PRODUCT_KEY,
  ILuggage,
  INSURANCE_PRODUCT_KEY,
  ITimePeriod,
  LegacyOrder,
  LegacyOrderLine,
  OrderRequest,
  PricingModel,
  WALK_IN_INSURANCE_FEE_PRODUCT_KEY,
} from '@luggagehero/shared/interfaces';
import { SharedErrorService } from '@luggagehero/shared/services/error';
import { SharedNotificationService } from '@luggagehero/shared/services/notification';
import { SharedOrderService } from '@luggagehero/shared/services/orders';
import { SharedPricingService } from '@luggagehero/shared/services/pricing';
import { SharedPromoCodeService } from '@luggagehero/shared/services/promo-codes';
import { SharedStorageCriteria, SharedStorageCriteriaService } from '@luggagehero/shared/services/storage-criteria';
import { cloneDeep, isEqual, SharedUtilOrder } from '@luggagehero/shared/util';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, skip, skipWhile, Subscription } from 'rxjs';

import { BookingActionBaseComponent } from './booking-action.base-component';

@Component({
  template: '',
  standalone: false,
})
export abstract class ModifyBookingActionBaseComponent
  extends BookingActionBaseComponent
  implements AfterViewInit, OnDestroy
{
  private translate = inject(TranslateService);
  private error = inject(SharedErrorService);
  private notify = inject(SharedNotificationService);
  private criteriaService = inject(SharedStorageCriteriaService);
  private priceService = inject(SharedPricingService);
  private promoCodeService = inject(SharedPromoCodeService);
  private orderService = inject(SharedOrderService);

  @ViewChild('datePickerAnchor') public datePickerAnchor: ElementRef;
  @ViewChild('datePickerPopup', { read: ElementRef }) public datePickerPopup: ElementRef;

  public modifiedOrder: LegacyOrder;
  public order: LegacyOrder;

  public hourlyRate: number;
  public dailyRate: number;
  public currency: string;

  public minBagsWarning: string;

  /** The model the booking is on now (AM-5115). */
  public currentPricingModel: PricingModel;
  /** The models this booking's location sells, from a quote of the booking as it stands (AM-5115). */
  public sellablePricingModels: PricingModel[] = [];

  private subscriptions: Subscription[];
  private updateOrderTimeout: NodeJS.Timeout;
  private _isUpdating = false;
  private _isModified = false;
  private isWalkIn: boolean;
  private isGuest: boolean;
  /** The app-wide pricing model when the modal opened, put back when it closes (AM-5115). */
  private pricingModelBeforeModal: PricingModel;
  /** Counts quotes, so a response that lands after a newer request is dropped (AM-5115). */
  private quoteSequence = 0;
  private sellableSequence = 0;
  /**
   * The quote request for the booking exactly as it stands, built when the modal opens by the same code that
   * builds every change's request (AM-5115). A change whose request equals it changes nothing.
   */
  private unchangedRequest: OrderRequest;

  public get promoCodeCta(): string {
    return this.booking?.price?.discountCode ? 'CHANGE_DISCOUNT_CODE' : 'HAVE_A_VOUCHER_OR_PROMO_CODE_MINI';
  }

  get isUpdating(): boolean {
    return this._isUpdating;
  }
  set isUpdating(value: boolean) {
    this._isUpdating = value;
    this.cd.markForCheck();
  }

  get isModified(): boolean {
    return this._isModified;
  }
  set isModified(value: boolean) {
    this._isModified = value;
    this.cd.markForCheck();
  }

  public get addOns(): BookingAddOns {
    return {
      insurance: this.priceService.insuranceSelected,
      freeCancellation: this.priceService.freeCancellationFeeSelected,
      securitySeals: this.priceService.numberOfSecuritySeals,
      // Don't change whether this was a walk-in or not
      walkIn: this.isWalkIn,
      guest: this.isGuest,
    };
  }

  public get isModifyBagsAllowed(): boolean {
    if (!this.booking) {
      return false;
    }
    return ['CONFIRMED', 'CHECKED_IN'].includes(this.booking.status);
  }

  /**
   * Whether the modal offers a switch between hourly and daily pricing (AM-5115): only before drop-off
   * (the API refuses it in any other status), only on a marketplace pre-booking (hotel-guest and walk-in
   * bookings keep the model their flow set), and only when the location sells both models.
   */
  public get isPricingModelSwitchAvailable(): boolean {
    if (!this.booking || this.booking.status !== 'CONFIRMED' || this.isGuest || this.isWalkIn) {
      return false;
    }
    return this.sellablePricingModels.includes('hourly') && this.sellablePricingModels.includes('daily');
  }

  public get isModifyDateAllowed(): boolean {
    if (!this.booking) {
      return false;
    }
    return this.booking.status === 'CONFIRMED';
  }

  public get isOpenOnDropoffDate(): boolean {
    const dropoffHours = this.shopsService.getOpeningHoursForDate(this.criteriaService.period.from, this.shop);
    if (!dropoffHours || dropoffHours.length === 0) {
      return false;
    }
    return true;
  }

  public get isOpenOnPickupDate(): boolean {
    const pickupHours = this.shopsService.getOpeningHoursForDate(this.criteriaService.period.to, this.shop);
    if (!pickupHours || pickupHours.length === 0) {
      return false;
    }
    return true;
  }

  public get canConfirmChanges(): boolean {
    if (!this.isOpenOnDropoffDate || !this.isOpenOnPickupDate) {
      return false;
    }
    return true;
  }

  public ngAfterViewInit() {
    // HACK: Using a delay to be sure all initialization is done as we only want to react to user-initiated changes
    setTimeout(() => {
      //
      // Subscribe to service changes to update the order accordingly.
      //
      this.subscriptions = [
        // Not `skip(1)`: the subject replays its CURRENT value, so a model chosen in the first second (the
        // selector shows as soon as the models sold are known) was swallowed and never quoted (AM-5115). Skip
        // only the booking's own model until the first real change; every change after that, back included,
        // reaches updateOrder, which quotes it unless it is the booking as it stands.
        this.priceService.pricingModel$
          .pipe(
            distinctUntilChanged(),
            skipWhile((model) => model === this.currentPricingModel),
          )
          .subscribe(() => this.updateOrder(false)),
        this.promoCodeService.promoCode.pipe(skip(1)).subscribe(() => this.updateOrder(false)),
        this.criteriaService.changeRequested.subscribe(() => this.updateOrder(false)),
      ];
    }, 1000);
  }

  public onBaseInit(): Promise<void> {
    //
    // Initialize service values to match the current state of the booking
    //
    this.priceService.insuranceSelected = this.booking.price.addOns.insurance ? true : false;
    this.priceService.freeCancellationFeeSelected = this.booking.price.addOns.freeCancellation ? true : false;
    this.priceService.numberOfSecuritySeals = this.booking.price.addOns.securitySeals || 0;
    // Set the current order from the booking (clone to avoid modifying the order stored on the booking)
    this.order = cloneDeep(this.booking.orderDetails[this.booking.orderDetails.length - 1]);

    // The model the booking is charged on is its storage line's; `price.pricingModel` is the fallback.
    this.currentPricingModel =
      SharedUtilOrder.storagePricingModel(this.order) ?? this.booking.price.pricingModel ?? 'hourly';
    this.pricingModelBeforeModal = this.priceService.pricingModel;
    this.priceService.changePricing(this.currentPricingModel);

    const luggage: ILuggage = {
      normal: (this.booking.metadata?.lh_number_of_bags || this.booking.luggage.normal) as number,
      hand: this.booking.luggage.hand,
    };
    const period: ITimePeriod = {
      from: this.booking.period.checkIn,
      to: this.booking.period.checkOut,
    };
    this.criteriaService.current = new SharedStorageCriteria(
      this.criteriaService.currentOrDefault.location,
      period,
      luggage,
    );

    // Record whether the existing order was a walk-in
    this.isWalkIn = this.order.orderRequest.addOns.walkIn;
    this.isGuest = this.order.orderRequest.addOns.guest;

    this.minBagsWarning = this.criteriaService.getMinimumBagsWarning(
      this.criteriaService.currentOrDefault.luggage,
      this.isWalkIn || this.isGuest,
    );

    this.unchangedRequest = this.buildRequestForQuote(this.currentPricingModel);

    void this.loadSellablePricingModels();

    return void 0;
  }

  public ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }
    // The pricing service is app-wide: the model chosen here must not carry into a later search or booking.
    if (this.pricingModelBeforeModal) {
      this.priceService.changePricing(this.pricingModelBeforeModal);
    }
    super.ngOnDestroy();
  }

  public onOrderLineChange(orderLine: LegacyOrderLine) {
    // HACK: Using a hardcoded product id to add/remove insurance for now
    switch (orderLine.product._id) {
      case WALK_IN_INSURANCE_FEE_PRODUCT_KEY:
      case GUEST_INSURANCE_FEE_PRODUCT_KEY:
      case GUEST_PREMIUM_INSURANCE_FEE_PRODUCT_KEY:
      case INSURANCE_PRODUCT_KEY:
        this.priceService.insuranceSelected = orderLine.selected ? true : false;
        break;
    }

    this.updateOrder(false);
  }

  public async confirmChanges() {
    // The templates disable Confirm in these states too; this holds if a click lands anyway. While a quote is
    // in flight `modifiedOrder` may not be the change on screen yet, and with no change there is nothing to
    // commit (AM-5115).
    if (this.isLoading || this.isUpdating || !this.isModified || !this.modifiedOrder) {
      return;
    }
    this.isLoading = true;

    try {
      // The quote this modal shows, not the order service's latest: the sellable-models quote can land after it.
      const res = await this.bookingService.modifyBooking(this.booking._id, this.modifiedOrder._id);

      this.updateBooking(res);
      this.close();

      this.notify.chat(
        this.translate.instant('BOOKING_WAS_CHANGED_SUCCESSFULLY') as string,
        this.translate.instant('BOOKING_CHANGED') as string,
      );
    } catch (err) {
      this.error.handleError(err, this.translate.instant('BOOKING_COULD_NOT_BE_CHANGED') as string);
    }

    this.isLoading = false;
  }

  /**
   * Quote the booking under each pricing model, to learn which ones its location sells for it and what
   * each would charge per bag (AM-5115). Neither quote is committed; confirming commits the quote of the
   * change the customer made. Run when the modal opens and, where the selector shows rates, after every
   * change, so the rates follow the bags and dates. A failure leaves the switch hidden: the modal then
   * offers what it offered before.
   */
  private async loadSellablePricingModels(): Promise<void> {
    if (this.booking.status !== 'CONFIRMED' || this.isGuest || this.isWalkIn) {
      return;
    }
    const sequence = ++this.sellableSequence;
    try {
      const quoteFor = (model: PricingModel) =>
        this.orderService.generateQuote({ ...this.buildRequestForQuote(model), bookingId: this.booking._id });
      const [hourly, daily] = await Promise.all([quoteFor('hourly'), quoteFor('daily')]);
      if (sequence !== this.sellableSequence) {
        return;
      }
      const sellable = SharedUtilOrder.sellablePricingModels(
        { hourly, daily },
        AppConfig.SHOPS_WITH_HOURLY_RATE_DISABLED.includes(this.booking.shopId),
      );
      this.sellablePricingModels = sellable.models;
      // The same setting decides whether the booking flow's selector shows a price on its buttons; when it
      // is off the price shows only in the order summary below (AM-5115).
      if (AppConfig.IS_PRICE_ON_PRICING_MODEL_SELECTOR_ENABLED) {
        this.hourlyRate = sellable.rates.hourly;
        this.dailyRate = sellable.rates.daily;
      }
      this.currency = (daily ?? hourly)?.productList?.currency ?? this.currency;
    } catch {
      if (sequence === this.sellableSequence) {
        this.sellablePricingModels = [];
      }
    }
    this.cd.markForCheck();
  }

  private buildRequestForQuote(pricingModel: PricingModel): OrderRequest {
    const appliedDiscountCode = this.promoCodeService.appliedDiscount?.code;
    let discountCode = this.booking.price.discountCode;

    // Check if we have an applied discount locally and it's not equal to the discount currently on the booking
    if (appliedDiscountCode && appliedDiscountCode !== discountCode) {
      discountCode = appliedDiscountCode;
    }

    const luggage = this.criteriaService.sanitizeLuggage(
      this.criteriaService.currentOrDefault.luggage,
      this.isWalkIn || this.isGuest,
    );

    return this.orderService.getRequestForQuote(
      this.booking.shopId,
      pricingModel,
      luggage,
      this.criteriaService.currentOrDefault.period,
      this.addOns,
      this.booking.orderDetails[0]?.orderRequest?.optionalProducts,
      discountCode,
    );
  }

  private updateOrder(immediate = false) {
    if (!this.isInitialized) {
      return;
    }

    this.minBagsWarning = this.criteriaService.getMinimumBagsWarning(
      this.criteriaService.currentOrDefault.luggage,
      this.isWalkIn || this.isGuest,
    );

    const requestForQuote = this.buildRequestForQuote(this.priceService.pricingModel);

    // Compared with the request the modal built for the booking when it opened, not one rebuilt from the
    // booking's own fields: those differ in shape (the booking's add-ons carry a tip and no walk-in or guest
    // flag), so the old comparison (`orderService.hasChanged`) never saw an undone change as no change.
    this.isModified = !isEqual(requestForQuote, this.unchangedRequest);

    if (!this.isModified) {
      // Back on every one of the booking's current options (say hourly -> daily -> hourly): quote nothing,
      // since prices may have moved since it was booked, and show the order the booking already has. Any
      // debounced or in-flight quote of the change just undone is dropped (AM-5115).
      clearTimeout(this.updateOrderTimeout);
      this.quoteSequence++;
      this.modifiedOrder = undefined;
      this.isUpdating = false;
      return;
    }

    if (immediate) {
      void this._updateOrder(requestForQuote);
      return;
    }

    this.isUpdating = true;

    clearTimeout(this.updateOrderTimeout);

    const timeout = setTimeout(() => {
      void (async () => {
        await this._updateOrder(requestForQuote);
        // Only the latest change ends "updating" (AM-5115). A quote landing after a newer change was made (in
        // flight or still in its debounce) must not enable Confirm while the summary is not the change on screen.
        if (timeout === this.updateOrderTimeout) {
          this.isUpdating = false;
        }
      })();
    }, AppConfig.ORDER_DEBOUNCE_TIME);
    this.updateOrderTimeout = timeout;
  }

  private async _updateOrder(requestForQuote: OrderRequest) {
    // Names the booking, so the API keeps its storage product (AM-5020). Added here rather than to the request
    // compared with `unchangedRequest`, which has no id.
    const sequence = ++this.quoteSequence;
    const quote = await this.orderService.generateQuote({ ...requestForQuote, bookingId: this.booking._id });
    if (sequence !== this.quoteSequence) {
      return; // a newer change was quoted while this one was in flight; confirming must commit that one
    }
    this.modifiedOrder = quote;
    this.currency = quote?.productList?.currency ?? this.currency;

    // The per-bag rates on the selector follow the bags and dates the customer just changed. Only where the
    // selector shows rates: otherwise the models sold were settled when the modal opened (AM-5115).
    if (AppConfig.IS_PRICE_ON_PRICING_MODEL_SELECTOR_ENABLED) {
      void this.loadSellablePricingModels();
    }

    this.isModified = true;
  }
}
